import 'react-toastify/dist/ReactToastify.css'

import { getReplayId, ovalModeActive, replayModeActive, tvModeActive } from '@classes/app_url_params'
import { getPages } from '@config'
import { mainKeys } from '@constants/main_keys'
import type { AppState } from '@constants/types'
import { getDepthChartLimit } from '@constants/utils'
import worldJson from '@constants/world.json'
import useApi from '@hooks/useApi'
import useDashboard from '@hooks/useDashboard'
import useSocket from '@hooks/useSocket'
import useVesselPosition from '@hooks/useVesselPosition'
import { useYisNavigate } from '@hooks/useYisNavigate'
import Logger from '@logger'
import type { VesselConfig } from '@shared_types/config_types'
import type { MetricName } from '@shared_types/modbus_types'
import type { Metric, ReplayMetric } from '@shared_types/vessel_data'
import type { MekkaDirection } from '@shared_types/yis_types'
import { isNonEmptyArray } from '@shared_utils/array_utils'
import { load, loadHistory, update, updateHistory } from '@store/actions'
import { getSunsetAndSunrise } from '@utils/time_utils'
import type { FeatureCollection, GeoJsonProperties } from 'geojson'
import { forEach, isNumber } from 'lodash'
import moment from 'moment-timezone'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as topojson from 'topojson-client'
import type { GeometryCollection, Objects, Topology } from 'topojson-specification'

import Meter from '../Meter/Meter'
import type { ClusterVariant } from './styles'
import { ClusterContainer, DashLabel, GaugeItem } from './styles'
import type { IGaugeClusterProps, SunEventTimestamps } from './types'

type DashboardProps = IGaugeClusterProps

const key1 = 'vesselMaxSpeed'
const key2 = 'vesselMaxDepth'

const logger = Logger.create('Dashboard')

export default function Dashboard (props: DashboardProps) {
  const { t } = useTranslation()
  const { apiGet } = useApi()
  const navigate = useYisNavigate()
  const location = useLocation()
  const position = useVesselPosition()
  const ovalEnabled = ovalModeActive()
  const isUnmounted = useRef(false)
  const tvMode = tvModeActive()

  const main = useSelector((appState: AppState) => appState.main)
  const data = useSelector((appState: AppState) => appState.history.depth)
  const tempUnit = useSelector((appState: AppState) => appState.units.tempUnit)

  const dispatch = useDispatch()

  const [vesselMaxSpeed, setVesselMaxSpeed] = useState(localStorage.getItem(key1) || 1)
  const [vesselMaxDepth, setVesselMaxDepth] = useState(localStorage.getItem(key2) || 1)
  const [meccaLocation, setMeccaLocation] = useState(0)
  const [meccaDistance, setMeccaDistance] = useState(0)

  const [world] = useState<FeatureCollection>(
    topojson.feature(
      worldJson as unknown as Topology<Objects<GeoJsonProperties>>,
      worldJson.objects.countries as GeometryCollection<GeoJsonProperties>
    )
  )

  const [sunRoad, setSunRoad] = useState<SunEventTimestamps>({ sunrise: 0, sunset: 0 })

  const {
    showLabels,
    showWind,
    layout,
    showSpeed,
    showOrientation,
    showPressure,
    showDepth,
    hide,
    showWeather,
    showTime,
    showMecca,
    showEarth,
    grid,
    ovalLeft,
    ovalRight,
    showEcoScore
  } = props

  const disabledPages = getPages().filter(p => p.page && p.disabled)

  const showGaugeProps = {
    showWind,
    showSpeed,
    showOrientation,
    showPressure,
    showDepth,
    showWeather,
    showTime,
    showMecca,
    showEarth,
    data,
    tempUnit,
    showEcoScore
  }
  const stateItems = {
    vesselMaxSpeed,
    vesselMaxDepth,
    meccaLocation,
    meccaDistance,
    world,
    sunRoad
  }

  const items = useDashboard(main, showGaugeProps, stateItems)

  const replayedTripId = getReplayId()
  const inTripReplay = replayedTripId > 0

  const fetchMeccaLocation = () => {
    apiGet<MekkaDirection>('api/mecca/location').then(({ bearing, distance }) => {
      if (!isUnmounted.current) {
        setMeccaLocation(bearing)
        setMeccaDistance(distance)
      }
    })
  }

  const onMeterClick = (path: string | undefined, from?: string, disabled?: boolean) => () => {
    if (disabled) return null

    if (path && location.pathname !== path) {
      if (from) {
        navigate(path, { state: { from } })
      } else {
        navigate(path)
      }
    }
    return null
  }

  useEffect(() => {
    apiGet<VesselConfig>('/api/vesselInformation').then(async ({ maxSpeed, maxDepth }) => {
      if (isUnmounted.current) {
        return
      }

      setVesselMaxSpeed(maxSpeed)
      setVesselMaxDepth(maxDepth)

      localStorage.setItem(key1, `${maxSpeed}`)
      localStorage.setItem(key2, `${maxDepth}`)

      if (isUnmounted.current) {
        return
      }
      const { sunrise, sunset } = getSunsetAndSunrise(position, moment().format())
      if (sunrise && sunset) {
        setSunRoad({ sunrise: sunrise.getTime(), sunset: sunset.getTime() })
      }

      dispatch(load('main'))
      dispatch(loadHistory('depth', { limit: getDepthChartLimit() }))
      fetchMeccaLocation()

      if (replayedTripId > 0) {
        // request trip playback to be active
        void apiGet(`/api/trip-replay/start/${replayedTripId}`)
        logger.verbose(`Started trip replay [id=${replayedTripId}]`)
      }
    })
  }, [])

  const updateMetric = (key: MetricName, metric: Metric) => {
    const updateValue = { [key]: metric }
    logger.verbose(`metric updated ${JSON.stringify(updateValue)}`)
    dispatch(update(key, updateValue))
  }

  useSocket<ReplayMetric>(`trip-data-${replayedTripId}`, replayMetric => {
    if (isUnmounted.current || !inTripReplay) {
      return
    }

    const timestamp = replayMetric.date

    forEach(replayMetric, (value, key) => {
      if (key === 'lat' || key === 'lng') {
        key = 'position'
        // @ts-expect-error types error
        value = { lat: replayMetric.lat, lng: replayMetric.lng }
      }

      if (key !== 'date') {
        // @ts-expect-error types error
        updateMetric(key as MetricName, { value, timestamp })
      }
    })
  })

  for (const key of mainKeys) {
    useSocket<Metric>(`main.${key}`, metric => {
      if (!inTripReplay) {
        updateMetric(key as MetricName, metric)
        if (key === 'heading') {
          fetchMeccaLocation()
        }
      }
    })
  }

  useSocket<Metric[]>('history.depth', o => {
    if (!inTripReplay) {
      dispatch(updateHistory('depth', o))
    }
  })

  const updateHeading = () => {
    const value = Math.floor(Math.random() * 361)
    const timestamp = new Date().toISOString()
    updateMetric('heading', { value, timestamp })
  }

  useEffect(() => {
    if (replayModeActive()) {
      const intervalId = setInterval(updateHeading, 2000)

      return () => {
        clearInterval(intervalId)
      }
    }

    return
  }, [])

  useEffect(() => {
    return () => {
      isUnmounted.current = true
    }
  }, [])

  const initRotate = -30
  const rotateStep = 30
  const transOffset = 8
  let trans = 50

  let actRotate = initRotate
  let gaugeCounter = 0

  // Two-sided dashboard in oval mode config
  const flexExtOffset = '7dvw'
  const flexMiddleOffset = '0vw'

  let clusterVariant: ClusterVariant

  if (hide) {
    clusterVariant = 'hide'
  } else if (layout !== 'grid') {
    clusterVariant = 'flex'
  } else if (ovalEnabled) {
    clusterVariant = 'ovalCircle'
  } else {
    clusterVariant = 'grid'
  }

  return <ClusterContainer variant={clusterVariant}>
    {items
      .filter(item => isNonEmptyArray(item.values))
      .sort((a, b) => a.order - b.order)
      .map((dash, index) => {
        return dash.values.map(gauge => {
          let style
          let transformations
          let ovalItem = false
          let gridItem = false
          let gridMiddle = false
          style = {
            height: index === 4 ? '68vh' : grid === '4 x 1 x 4' ? '15vh' : '20vh'
          }

          if (ovalLeft || ovalRight) {
            gauge.id = `oval-${gauge.id}`
          }

          if (layout) {
            gauge.id = `grid-${gauge.id}`
          }

          if (ovalEnabled && layout === 'grid') {
            ovalItem = true
            if (index !== 4) {
              if (gaugeCounter % 2 === 0 && gaugeCounter !== 0) {
                actRotate = actRotate + rotateStep
              }

              actRotate = -actRotate
              trans = -trans
              const tOffset = actRotate % 180 !== 0 ? 0 : trans > 0 ? transOffset : -transOffset
              const finalTranslate = trans + tOffset
              transformations = {
                transform: `rotate(${actRotate}deg) translate(${finalTranslate}vh) rotate(${-actRotate}deg)`
              }
              gaugeCounter++
            }
          } else {
            if (layout === 'grid') {
              if (index === 4) {
                gridMiddle = true
              }
              gridItem = true
            } else {
              style = {
                height: tvMode ? 'calc((100vh - 7rem) / 6)' : 'calc((100dvh - 7rem) / 6)',
                margin: '0.5rem 0'
              }
            }

            if (ovalEnabled) {
              if (ovalRight) {
                style = {
                  ...style,
                  marginRight: gaugeCounter % 2 === 0 ? flexExtOffset : 0,
                  marginLeft: gaugeCounter % 2 === 1 ? flexMiddleOffset : 0
                }
              } else if (ovalLeft) {
                style = {
                  ...style,
                  marginLeft: gaugeCounter % 2 === 0 ? flexExtOffset : 0,
                  marginRight: gaugeCounter % 2 === 1 ? flexMiddleOffset : 0
                }
              }
            }
            gaugeCounter++
          }

          const linkDisabled = disabledPages.find(p => p.key === dash.key)?.disabled

          return <GaugeItem
            key={`meter-${index}`}
            onClick={onMeterClick(dash.path, dash?.from, linkDisabled)}
            ovalItem={ovalItem}
            gridItem={gridItem}
            gridMiddle={gridMiddle}
            style={transformations}
          >
            {isNumber(gauge.maxValue) && <Meter style={style} layout={layout} index={index} backgroundColor={'transparent'} {...gauge} />}

            {showLabels && <DashLabel variant="body2">
              {t(dash.label || '')}
            </DashLabel>}
          </GaugeItem>
        })
      })}
  </ClusterContainer>
}
